// Inserting dictated words into a field at the cursor, the way typing would:
// the field's own input event fires, so React state, react-hook-form and the
// rich-text editor all see the change. While the field has focus the browser's
// insertText command is used (it keeps Ctrl+Z working); otherwise, or where
// the command is unavailable, the value is changed directly.

export type TextControl = HTMLInputElement | HTMLTextAreaElement;
export type DictationTarget = TextControl | HTMLElement;

export const isTextControl = (
  element: DictationTarget,
): element is TextControl =>
  element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;

// Spaces around the dictated words so they never glue onto the neighbours.
export const spacedPiece = (
  before: string,
  text: string,
  after: string,
): string => {
  const words = text.trim();
  if (!words) return "";
  const lead = before && !/\s$/.test(before) ? " " : "";
  const trail = after && !/^[\s.,;:!?)]/.test(after) ? " " : "";
  return lead + words + trail;
};

const execInsertText = (piece: string): boolean => {
  try {
    return (
      typeof document.execCommand === "function" &&
      document.execCommand("insertText", false, piece) === true
    );
  } catch {
    return false;
  }
};

// React tracks the value through the instance property; the prototype
// setter changes it underneath so React's onChange fires on the input event.
const setNativeValue = (element: TextControl, value: string): void => {
  const prototype = Object.getPrototypeOf(element) as object;
  const setter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;
  if (setter) setter.call(element, value);
  else element.value = value;
};

export type Selection = { start: number; end: number };

/**
 * Inserts `text` into an input or textarea at `at` (where the cursor is; the
 * caller follows the cursor as the user moves it). Returns the cursor
 * position after the inserted words.
 */
export const insertIntoTextControl = (
  element: TextControl,
  text: string,
  at: Selection,
): number => {
  const focused = document.activeElement === element;
  const value = element.value;
  const clamp = (n: number): number => Math.max(0, Math.min(n, value.length));
  const start = clamp(at.start);
  const end = Math.max(start, clamp(at.end));
  const before = value.slice(0, start);
  const after = value.slice(end);
  let piece = spacedPiece(before, text, after);
  // Respect the field's maxLength, as typing would.
  if (element.maxLength > 0) {
    piece = piece.slice(
      0,
      Math.max(0, element.maxLength - before.length - after.length),
    );
  }
  if (!piece) return start;
  const caret = start + piece.length;
  const expected = before + piece + after;
  if (focused) element.setSelectionRange(start, end);
  if (!(focused && execInsertText(piece) && element.value === expected)) {
    setNativeValue(element, expected);
    element.dispatchEvent(new Event("input", { bubbles: true }));
  }
  if (document.activeElement === element) {
    element.setSelectionRange(caret, caret);
    // A controlled field may re-render its value after this event, which
    // puts the cursor at the end: put it back after the words.
    queueMicrotask(() => {
      if (document.activeElement === element && element.value === expected) {
        element.setSelectionRange(caret, caret);
      }
    });
  }
  return caret;
};

const textNodes = (root: HTMLElement): Text[] => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    nodes.push(node as Text);
  }
  return nodes;
};

const selectionRangeIn = (root: HTMLElement): Range | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  return root.contains(range.startContainer) ? range : null;
};

/** Character offset of the cursor in a contenteditable (null: elsewhere). */
export const caretOffsetIn = (root: HTMLElement): number | null => {
  const range = selectionRangeIn(root);
  if (!range) return null;
  const prefix = document.createRange();
  prefix.selectNodeContents(root);
  prefix.setEnd(range.startContainer, range.startOffset);
  return prefix.toString().length;
};

const placeCaret = (node: Node, offset: number): void => {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

/** Puts the cursor at a character offset (end when past the text). */
export const restoreCaretIn = (root: HTMLElement, offset: number): void => {
  let remaining = offset;
  for (const node of textNodes(root)) {
    if (remaining <= node.length) {
      placeCaret(node, remaining);
      return;
    }
    remaining -= node.length;
  }
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(root);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

const BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "LI",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "PRE",
  "BLOCKQUOTE",
  "TD",
]);

// The paragraph (or list item...) holding a point, for spacing decisions:
// text in the next paragraph is not "after" the words.
const blockOf = (root: HTMLElement, node: Node): Node => {
  for (let current: Node | null = node; current; current = current.parentNode) {
    if (current === root) return root;
    if (current instanceof HTMLElement && BLOCK_TAGS.has(current.tagName)) {
      return current;
    }
  }
  return root;
};

const textAround = (
  root: HTMLElement,
  node: Node,
  offset: number,
): { before: string; after: string } => {
  const block = blockOf(root, node);
  const before = document.createRange();
  before.selectNodeContents(block);
  before.setEnd(node, offset);
  const after = document.createRange();
  after.selectNodeContents(block);
  after.setStart(node, offset);
  return { before: before.toString(), after: after.toString() };
};

// The text node and offset at a character offset (null: no text yet).
const pointAt = (
  root: HTMLElement,
  offset: number,
): { node: Text; offset: number } | null => {
  let remaining = offset;
  const nodes = textNodes(root);
  // Line breaks between paragraphs in the HTML are not a place to write.
  const writable = (node: Text): boolean =>
    node.parentNode !== root || node.data.trim() !== "";
  for (const node of nodes) {
    if (!writable(node)) {
      if (remaining < node.length) break;
      remaining -= node.length;
      continue;
    }
    if (remaining <= node.length) return { node, offset: remaining };
    remaining -= node.length;
  }
  const last = nodes.filter(writable).at(-1);
  return last ? { node: last, offset: last.length } : null;
};

/**
 * Inserts `text` into a contenteditable editor at the cursor, or at the
 * character `fallbackOffset` when the cursor is not in it. Returns the
 * character offset after the inserted words.
 */
export const insertIntoContentEditable = (
  root: HTMLElement,
  text: string,
  fallbackOffset: number,
): number => {
  const total = (root.textContent ?? "").length;
  const focused = root.contains(document.activeElement);
  // The selection stays in the editor after a click on the mic or a blur
  // to the page; it is where the cursor was.
  const live = selectionRangeIn(root);
  if (live && !live.collapsed) live.deleteContents();
  const liveOffset = live ? caretOffsetIn(root) : null;
  const offset = Math.min(liveOffset ?? fallbackOffset, total);

  let point: { node: Node; offset: number } | null = live
    ? { node: live.startContainer, offset: live.startOffset }
    : pointAt(root, offset);
  const around = point
    ? textAround(root, point.node, point.offset)
    : { before: "", after: "" };
  const piece = spacedPiece(around.before, text, around.after);
  if (!piece) return offset;

  if (live && focused && execInsertText(piece)) {
    if ((root.textContent ?? "").length !== total) {
      return caretOffsetIn(root) ?? offset + piece.length;
    }
  }

  let caretNode: Node;
  let caretAt: number;
  if (point && point.node.nodeType !== Node.TEXT_NODE) {
    // Cursor between elements: a new text node there.
    const node = document.createTextNode(piece);
    const range = document.createRange();
    range.setStart(point.node, point.offset);
    range.insertNode(node);
    point = null;
    caretNode = node;
    caretAt = piece.length;
  } else if (point) {
    (point.node as Text).insertData(point.offset, piece);
    caretNode = point.node;
    caretAt = point.offset + piece.length;
  } else {
    // Empty editor: the words become its first line.
    const node = document.createTextNode(piece);
    root.appendChild(node);
    caretNode = node;
    caretAt = piece.length;
  }
  // Keep the cursor after the words when it was in the editor.
  const keepCaret = focused || live !== null;
  if (keepCaret) placeCaret(caretNode, caretAt);
  root.dispatchEvent(new Event("input", { bubbles: true }));
  // The editor may re-render its HTML on change: place the cursor again
  // by character offset in whatever DOM is there now.
  const caret = offset + piece.length;
  if (keepCaret && !root.contains(caretNode)) restoreCaretIn(root, caret);
  return caret;
};
